import { Tooltip } from 'components/ui';
import { subscriptionAccess } from 'config';
import type { LocationDescriptorObject } from 'history';
import querystring from 'querystring';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetStoreInfoQuery } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';
import { useProviderIcon } from './useProviderIcon';

interface ProviderCardProps {
  provider: string;
  description: string;
  linkTo: LocationDescriptorObject;
  disableConnect?: boolean;
  comingSoon?: boolean;
}

export const ProviderCard = ({
  provider,
  description,
  linkTo,
  disableConnect = false,
  comingSoon = false,
}: ProviderCardProps) => {
  const { query } = useParseURLQueryParams();
  const history = useHistory();
  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { ProviderIcon } = useProviderIcon({ provider });

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  const isProviderConnected = storeInfo?.integrations.find(
    (item) => item.provider.toLowerCase() === provider.toLowerCase()
  );

  const { storeSubscriptionPlan } = useStoreSubscriptionPlan();

  const handleConnect = () => {
    if (storeSubscriptionPlan < subscriptionAccess.addIntegration) {
      return toast.info('Integration requires an upgraded plan', {
        toastId: 'upgrade-provider',
      });
    }
    history.replace({ ...linkTo });
  };

  return (
    <div className="p-10 bg-white border shadow-sm w-segments-308px h-[355px] rounded-2xl">
      <div>{ProviderIcon}</div>
      <div className="flex flex-col justify-between h-full py-8">
        <div>
          <p className="font-bold">{provider}</p>
          <p className="mt-2 text-black text-opacity-30">{description}</p>
        </div>
        {isProviderConnected ? (
          <div className="flex items-center justify-center font-bold text-white border opacity-50 w-segments-224px h-segments-52px border-segments-accent-color-1 rounded-2xl bg-segments-accent-color-1">
            Connected
          </div>
        ) : (
          <div className="my-0.5">
            <Tooltip
              disabled={!!!disableConnect}
              text="Only one email marketing app can be integrated at a time. Disable your current integration to enable this one."
            >
              <button
                className="flex items-center justify-center font-bold border w-segments-224px h-segments-52px border-segments-accent-color-1 text-segments-accent-color-1 rounded-2xl focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleConnect}
                disabled={disableConnect || comingSoon}
              >
                {comingSoon ? 'Coming soon' : 'Connect'}
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
